import apiClient from './api';
import { getApiAccessToken } from './MapComponentsService';
import { GeometrySearchFilters, GeometrySearchResult } from '../types/GeometrySearch';
import { RIMSPolyline } from '../types/Polylines';
import { Point } from '../types/Point';
import { Polygon } from '../types/Polygon';
import { GeometryStatus } from '../types/BaseGeometry';

const baseUrl = '/api/Geometry';

export interface SearchGeometriesResponse {
  results: GeometrySearchResult[];
  polylines: RIMSPolyline[];
  points: Point[];
  polygons: Polygon[];
}

/**
 * Search geometries based on multiple filter criteria
 * This service aggregates all geometry types and applies client-side filtering
 */
export const searchGeometries = async (
  addressSystemId: number,
  filters: GeometrySearchFilters
): Promise<SearchGeometriesResponse> => {
  const token = await getApiAccessToken();
  
  // Fetch all geometries based on contract status
  let allPolylines: RIMSPolyline[] = [];
  let allPoints: Point[] = [];
  let allPolygons: Polygon[] = [];
  let geometryStatuses: GeometryStatus[] = [];

  try {
    // If specific contracts are selected, fetch for each
    if (filters.selectedContractIds.length > 0) {
      const polylinePromises = filters.selectedContractIds.map(contractId =>
        apiClient.get(`${baseUrl}/getAllPolylines/${addressSystemId}/${contractId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      const pointPromises = filters.selectedContractIds.map(contractId =>
        apiClient.get(`${baseUrl}/getAllPolypoints/${addressSystemId}/${contractId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      const polygonPromises = filters.selectedContractIds.map(contractId =>
        apiClient.get(`${baseUrl}/getAllPolygons/${addressSystemId}/${contractId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );

      if (filters.geometryType === 'all' || filters.geometryType === 'polyline') {
        const polylineResponses = await Promise.all(polylinePromises);
        allPolylines = polylineResponses.flatMap(res => res.data as RIMSPolyline[]);
      }
      if (filters.geometryType === 'all' || filters.geometryType === 'point') {
        const pointResponses = await Promise.all(pointPromises);
        allPoints = pointResponses.flatMap(res => res.data as Point[]);
      }
      if (filters.geometryType === 'all' || filters.geometryType === 'polygon') {
        const polygonResponses = await Promise.all(polygonPromises);
        allPolygons = polygonResponses.flatMap(res => res.data as Polygon[]);
      }
    } else {
      // Fetch all geometries for the address system
      if (filters.geometryType === 'all' || filters.geometryType === 'polyline') {
        const response = await apiClient.get(`${baseUrl}/getAllPolylinesByAddressSystem/${addressSystemId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        allPolylines = response.data as RIMSPolyline[];
      }
      if (filters.geometryType === 'all' || filters.geometryType === 'point') {
        const response = await apiClient.get(`${baseUrl}/getAllPolypointsByAddressSystem/${addressSystemId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        allPoints = response.data as Point[];
      }
      if (filters.geometryType === 'all' || filters.geometryType === 'polygon') {
        const response = await apiClient.get(`${baseUrl}/getAllPolygonsByAddressSystem/${addressSystemId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        allPolygons = response.data as Polygon[];
      }
    }

    geometryStatuses = await apiClient.get(`/api/Geometry/status/${addressSystemId}`, {
        headers: { Authorization: `Bearer ${token}` }
    }).then(
        (res) => res.data as GeometryStatus[]
    );

    // Convert to unified search result format
    const results: GeometrySearchResult[] = [];

    // Add polylines
    allPolylines.forEach(polyline => {
      results.push({
        id: polyline.id,
        featureId: polyline.featureId,
        name: polyline.name,
        type: 'polyline',
        contractId: polyline.contractId,
        contractName: polyline.contractName,
        foremanId: polyline.foremanId,
        foremanName: polyline.foremanName,
        startDate: polyline.startDate,
        statusDesc: polyline.statusDesc,
        wkt: polyline.wkt,
        addressSystemId: polyline.addressSystemId,
        fullGeometry: polyline 
      });
    });

    // Add points
    allPoints.forEach(point => {
      results.push({
        id: point.id,
        featureId: point.featureId,
        name: point.name,
        type: 'point',
        contractId: point.contractId,
        contractName: point.contractName,
        foremanId: point.foremanId,
        foremanName: point.foremanName,
        startDate: point.startDate,
        statusDesc: point.statusDesc,
        wkt: point.wkt,
        addressSystemId: point.addressSystemId,
        fullGeometry: point 
      });
    });

    // Add polygons
    allPolygons.forEach(polygon => {
      results.push({
        id: polygon.id,
        featureId: polygon.featureId,
        name: polygon.name,
        type: 'polygon',
        contractId: polygon.contractId,
        contractName: polygon.contractName,
        foremanId: polygon.foremanId,
        foremanName: polygon.foremanName,
        startDate: polygon.startDate,
        statusDesc: polygon.statusDesc,
        wkt: polygon.wkt,
        addressSystemId: polygon.addressSystemId,
        fullGeometry: polygon 
      });
    });

    // Apply filters
    let filteredResults = results;

    // Filter by geometry name
    if (filters.geometryName) {
      const searchTerm = filters.geometryName.toLowerCase();
      filteredResults = filteredResults.filter(r =>
        r.name.toLowerCase().includes(searchTerm)
      );
    }

    // Filter by foreman assignment
    if (filters.foremanId) {
      filteredResults = filteredResults.filter(r => r.foremanId === filters.foremanId);
    }

    // Filter by date range
    if (filters.fromDate) {
      filteredResults = filteredResults.filter(r => {
        if (!r.startDate) return false;
        const startDate = new Date(r.startDate);
        return startDate >= filters.fromDate!;
      });
    }

    if (filters.toDate) {
      filteredResults = filteredResults.filter(r => {
        if (!r.startDate) return false;
        const startDate = new Date(r.startDate);
        return startDate <= filters.toDate!;
      });
    }

    // Sort results
    if (filters.sortBy === 'name') {
      filteredResults.sort((a, b) => a.name.localeCompare(b.name));
    } else if (filters.sortBy === 'date') {
      filteredResults.sort((a, b) => {
        const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
        const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
        return dateB - dateA; // Most recent first
      });
    }

    // Filter by geometry status
    if (filters.geometryStatusId) {
        const validGeometryStatus = geometryStatuses.find(status => status.id === filters.geometryStatusId)
        filteredResults = filteredResults.filter(r => r.statusDesc?.trim() === validGeometryStatus?.description.trim());
    }

    // Separate filtered results back into their respective types
    const filteredPolylines = allPolylines.filter(polyline => 
      filteredResults.some(r => r.type === 'polyline' && r.id === polyline.id)
    );
    const filteredPoints = allPoints.filter(point => 
      filteredResults.some(r => r.type === 'point' && r.id === point.id)
    );
    const filteredPolygons = allPolygons.filter(polygon => 
      filteredResults.some(r => r.type === 'polygon' && r.id === polygon.id)
    );

    return {
      results: filteredResults,
      polylines: filteredPolylines,
      points: filteredPoints,
      polygons: filteredPolygons
    };
  } catch (error) {
    console.error('Error searching geometries:', error);
    throw error;
  }
};
